import { combineEpics, Epic } from 'redux-observable';
import { from, of, EMPTY } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap
} from 'rxjs/operators';
import { Position, Toaster, Intent } from '@blueprintjs/core';
import { isOfType } from 'typesafe-actions';

import {
  NoteflowActions,
  NoteflowActionTypes,
  executingNoteflow,
  executedNoteflow,
  executingNoteflowFailed,
  loadingNoteflowExecution,
  loadedNoteflowExecution,
  loadingNoteflowExecutionFailed,
  loadingNoteflowExecutions,
  loadedNoteflowExecutions,
  loadingNoteflowExecutionsFailed,
  loadedNoteflowExecutionLogs,
  loadingNoteflowExecutionLogs,
  loadingNoteflowExecutionLogsFailed,
  loadedSearchReport,
  loadingSearchReportFailed,
  loadedSearchUser,
  loadingSearchUserFailed,
  checkNoteflowExecution,
  loadNoteflowTaskResults,
  loadedNoteflowTaskResults,
  loadingNoteflowTaskResultsFailed,
  loadingNoteflowTaskResults,
  loadedReRuns,
  loadingReRuns,
  loadingReRunsFailed,
} from '../actions/noteflowActions';
import {setShowSubscriptionDialog} from '../actions/uiActions';
import { IState } from '../reducers';
import {
  getNoteflowExecutionLogs,
  getNoteflowExecution,
  getNoteflowExecutions,
  getReRuns,
  getNoteflowTaskResults,
} from '../../services/noteflowApi';
import {
  searchReport,
  searchUser,
} from '../../services/searchApi';

const noteflowToaster = Toaster.create({
  className: 'noteflow-toaster',
  position: Position.TOP
});

const getNoteflowExecutionEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_NOTEFLOW_EXECUTION)),
    switchMap((action: any) =>
      from(getNoteflowExecution(action.payload.flowRunId, action.payload.detailed)).pipe(
        map((response: any) => {
          return loadedNoteflowExecution(response.result);
        }),
        startWith(loadingNoteflowExecution()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          noteflowToaster.show({
            message: `An error occurred while loading executions: ${message}.`,
            intent: Intent.DANGER
          });

          return of(loadingNoteflowExecutionFailed());
        })
      )
    )
  );

const CHECK_FINISH_STATUS = ['success', 'failed', 'cancelled']
const checkNoteflowExecutionEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.CHECK_NOTEFLOW_EXECUTION)),
    mergeMap( // map or mergeMap?
      (action: any) => {
        const delayTime = 2000; // check every 2 seconds

        if (
          action.payload.source !== state$.value.report.uid
        ) {
          /*
           * If user changed page, no need to check for results anymore
           */
          return EMPTY;
        }

        return from(getNoteflowExecution(action.payload.uid, false))
          .pipe(
            delay(delayTime),
            map(({ result }: any) => {
              const finished =
                (result && result?.status && CHECK_FINISH_STATUS.indexOf(result?.status) >= 0) || false;

              if (
                action.payload.counter >= 900 ||
                !result ||
                finished
              ) {
                /*
                 * if no execution or we checked more than N times => Timeout
                 * or execution finished
                 */
                dispatch(executedNoteflow({}));

                if (finished) {
                  dispatch(loadNoteflowTaskResults(result.uid));
                }

                return loadedNoteflowExecution(result);
              }

              return checkNoteflowExecution(
                action.payload.uid,
                action.payload.source,
                action.payload.counter + 1
              );
            }),
            catchError((err: any) => {
              const message = err.response?.data?.message || "";

              noteflowToaster.show({
                message: `An error occurred while loading executions: ${message}.`,
                intent: Intent.DANGER
              });

              return of(loadingNoteflowExecutionFailed());
            }),
        )
      }
    )
  );

const getNoteflowExecutionsEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_NOTEFLOW_EXECUTIONS)),
    switchMap((action: any) =>
      from(getNoteflowExecutions(action.payload)).pipe(
        map((response: any) => {
          return loadedNoteflowExecutions(
            response.result,
            action.payload?.reload,
            action.payload?.page_size,
          );
        }),
        startWith(loadingNoteflowExecutions()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          noteflowToaster.show({
            message: `An error occurred while loading executions: ${message}.`,
            intent: Intent.DANGER
          });

          return of(loadingNoteflowExecutionsFailed());
        })
      )
    )
  );

const getReRunsEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  {dispatch}: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_RE_RUNS)),
    switchMap((action: any) =>
      from(getReRuns(action.payload.parentId, action.payload.flowRunId)).pipe(
        map((response: any) => {
          return loadedReRuns(response.result);
        }),
        startWith(loadingReRuns()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          noteflowToaster.show({
            message: `An error occurred while loading re-runs: ${message}.`,
            intent: Intent.DANGER
          });

          return of(loadingReRunsFailed());
        })
      )
    )
  );

const getNoteflowExecutionLogsEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_NOTEFLOW_EXECUTION_LOGS)),
    switchMap((action: any) =>
      from(getNoteflowExecutionLogs(action.payload)).pipe(
        map((response: any) => {
          return loadedNoteflowExecutionLogs(response.result);
        }),
        startWith(loadingNoteflowExecutionLogs()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          noteflowToaster.show({
            message: `An error occurred while loading execution logs: ${message}.`,
            intent: Intent.DANGER
          });

          return of(loadingNoteflowExecutionLogsFailed());
        })
      )
    )
  );

const getNoteflowExecutionTaskResultsEpic: Epic<NoteflowActions, NoteflowActions, IState, any> = (
  action$: any,
  state$: any,
  { dispatch }: any,
) => (
  /*
   * Get `query` and `block_execution` uid along with their `results_key`
   */
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_NOTEFLOW_TASK_RESULTS)),
    switchMap((action: any) =>
      from(getNoteflowTaskResults(action.payload)).pipe(
        map((response: any) => {
          return loadedNoteflowTaskResults(response.result);
        }),
        startWith(loadingNoteflowTaskResults()),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";

          noteflowToaster.show({
            message: `An error occurred while loading task results: ${message}.`,
            intent: Intent.DANGER
          });

          return of(loadingNoteflowTaskResultsFailed());
        })
      )
    )
  )
);

/*
 * Noteflow Filter actions
 * TODO - refactor this, move it into one common epic, add "target" or search "source" to the action
 */
const searchReportEpic: Epic<NoteflowActions, NoteflowActions, IState> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_SEARCH_REPORT)),
    switchMap((action: any) =>
      from(searchReport(action.payload)).pipe(
        map((response: any) => {
          return loadedSearchReport(response.result);
        }),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";
          console.log("[Noteflow] Error while searching report", message);

          return of(loadingSearchReportFailed());
        })
      )
    )
  );

const searchUserEpic: Epic<NoteflowActions, NoteflowActions, IState> = (
  action$: any,
  state$: any,
  { dispatch }: any
) =>
  action$.pipe(
    filter(isOfType(NoteflowActionTypes.LOAD_SEARCH_USER)),
    switchMap((action: any) =>
      from(searchUser(action.payload)).pipe(
        map((response: any) => {
          return loadedSearchUser(response.result);
        }),
        catchError((err: any) => {
          const message = err.response?.data?.message || "";
          console.log("[Noteflow] Error while searching noteuserbook", message);

          return of(loadingSearchUserFailed());
        })
      )
    )
  );

export default combineEpics(
  getNoteflowExecutionEpic,
  checkNoteflowExecutionEpic,
  getNoteflowExecutionsEpic,
  getReRunsEpic,
  getNoteflowExecutionLogsEpic,
  getNoteflowExecutionTaskResultsEpic,
  searchReportEpic,
  searchUserEpic,
);
